/**
 * DiaryVault.jsx — HealHub Diary & Quick Notes
 * Mobile-first. 80% of users are on mobile.
 *
 * FIX: EntryDetail body now uses white-space:pre-wrap so spaces/newlines
 *      from saved entries render correctly instead of running words together.
 *      Debug block removed.
 *
 * Three bottom-tab sections:
 *  Journal  — diary entries organized by month + day
 *  Quick Notes — mood logs (paid only, blurred teaser for free)
 *
 * API routes used:
 *  GET /api/diary/months
 *  GET /api/diary/month?month=YYYY-MM
 *  GET /api/quick-notes/month?month=YYYY-MM
 *
 * RAZORPAY: upgrade buttons call navigate('/pricing')
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link }           from 'react-router-dom';
import { auth }                        from '../firebase';
import { signOut }                     from 'firebase/auth';
import { authFetch, getUserClaims }    from '../utils/authFetch';

/* ─── mood palette ────────────────────────────────────────────── */
const MOOD = {
  happy      :{c:'#2d6a4f',bg:'#e8f5e4',bar:'#66bb6a',e:'😊'},
  grateful   :{c:'#2d6a4f',bg:'#e8f5e4',bar:'#66bb6a',e:'🙏'},
  hopeful    :{c:'#00695c',bg:'#e0f2f1',bar:'#26a69a',e:'🌱'},
  calm       :{c:'#1b6ca8',bg:'#e3f2fd',bar:'#42a5f5',e:'😌'},
  proud      :{c:'#4a148c',bg:'#f3e5f5',bar:'#ab47bc',e:'💪'},
  excited    :{c:'#8a5a12',bg:'#fff3e0',bar:'#ff9800',e:'🎉'},
  neutral    :{c:'#4e342e',bg:'#efebe9',bar:'#a1887f',e:'😐'},
  tired      :{c:'#4e342e',bg:'#efebe9',bar:'#8d6e63',e:'😴'},
  lonely     :{c:'#374151',bg:'#f1f5f9',bar:'#64748b',e:'🌧'},
  sad        :{c:'#4a3f8c',bg:'#ede7f6',bar:'#7e57c2',e:'😢'},
  anxious    :{c:'#b71c1c',bg:'#fce4ec',bar:'#ef5350',e:'😰'},
  overwhelmed:{c:'#880e4f',bg:'#fce4ec',bar:'#e91e63',e:'😩'},
  angry      :{c:'#b71c1c',bg:'#ffebee',bar:'#d32f2f',e:'😤'},
  crisis     :{c:'#1a4f8a',bg:'#e3f2fd',bar:'#2196f3',e:'💙'},
};
const ms = m => MOOD[m] || MOOD.neutral;

const DAY_NAMES  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MON_NAMES  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MON_SHORT  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtDate = ts => {
  if (!ts) return {dow:'',day:'',full:'',time:''};
  const d = new Date(ts);
  return {
    dow : DAY_NAMES[d.getDay()],
    day : d.getDate(),
    full: `${d.getDate()} ${MON_SHORT[d.getMonth()]} ${d.getFullYear()}`,
    time: d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true}),
    mon : d.getMonth(),
    year: d.getFullYear(),
    dateKey: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`,
  };
};

const groupByDay = items => {
  const map = {};
  items.forEach(it => {
    const {dow,day,full} = fmtDate(it.timestamp);
    const k = `${day}`;
    if (!map[k]) map[k] = {day,dow,full,items:[]};
    map[k].items.push(it);
  });
  return Object.values(map).sort((a,b) => b.day - a.day);
};

/* ─── sub-components ──────────────────────────────────────────── */
const MiniCalendar = ({month, activeDays, selDay, onDayClick}) => {
  if (!month) return null;
  const [y,m] = month.split('-').map(Number);
  const first  = new Date(Date.UTC(y,m-1,1)).getUTCDay();
  const total  = new Date(Date.UTC(y,m,0)).getUTCDate();
  const wds    = ['S','M','T','W','T','F','S'];
  const cells  = [];
  for (let i=0;i<first;i++) cells.push(null);
  for (let d=1;d<=total;d++) cells.push(d);
  
  const has = d => activeDays.includes(d);

  return (
    <div style={{padding:'0 16px 12px'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'3px',marginBottom:'4px'}}>
        {wds.map((w,i) => (
          <div key={i} style={{textAlign:'center',fontSize:'10px',fontWeight:500,color:'#a0917d',padding:'2px 0'}}>{w}</div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'3px'}}>
        {cells.map((d,i) => {
          if (!d) return <div key={i}/>;
          const active = has(d);
          const isSelected = d === selDay; // <-- NEW: Check if this day is filtered
          
          return (
            <div
              key={i}
              onClick={() => active && onDayClick(d)}
              style={{
                textAlign:'center',padding:'5px 2px',borderRadius:'8px',
                fontSize:'11px',fontWeight:active?700:400,
                // NEW: Use solid orange if selected, otherwise keep your exact original colors
                color: isSelected ? '#fff' : (active ? '#c95b3d' : '#c8bfb2'),
                background: isSelected ? '#c95b3d' : (active ? '#fdf2ee' : 'transparent'),
                cursor:active?'pointer':'default',
                position:'relative',
              }}
            >
              {d}
              {/* NEW: Hide your little dot if the day is selected, show it if active but not selected */}
              {active && !isSelected && (
                <div style={{
                  position:'absolute',bottom:'2px',left:'50%',transform:'translateX(-50%)',
                  width:'3px',height:'3px',borderRadius:'50%',background:'#c95b3d',
                }}/>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EntryCard = ({entry, onClick}) => {
  const m    = ms(entry.mood);
  const {time} = fmtDate(entry.timestamp);
  const prev = (entry.text||entry.entry||'').slice(0,110);
  return (
    <div
      onClick={onClick}
      style={{
        margin:'0 16px 10px',borderRadius:'18px',
        border:'1px solid #e8e2d6',background:'#fff',
        overflow:'hidden',cursor:'pointer',
        WebkitTapHighlightColor:'transparent',
        transition:'transform .18s',
      }}
      onTouchStart={e=>e.currentTarget.style.transform='scale(.98)'}
      onTouchEnd={e=>e.currentTarget.style.transform='scale(1)'}
    >
      {/* mood bar top */}
      <div style={{height:'3px',background:m.bar,width:'100%'}}/>
      <div style={{padding:'14px 16px 12px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'8px',marginBottom:'7px'}}>
          <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'16px',fontWeight:700,color:'#1a1612',lineHeight:1.3,flex:1}}>
            {entry.title||'Untitled Entry'}
          </div>
          <div style={{
            fontSize:'10px',fontWeight:500,padding:'3px 10px',borderRadius:'20px',
            background:m.bg,color:m.c,flexShrink:0,marginTop:'2px',
          }}>
            {m.e} {entry.mood}
          </div>
        </div>
        <div style={{fontSize:'13px',color:'#8c7f6d',lineHeight:'1.6',
          overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',
          marginBottom:'9px',fontWeight:300}}>
          {prev}{prev.length>=110?'...':''}
        </div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:'11px',color:'#bfb5a6'}}>{time}</div>
          <div style={{fontSize:'11px',fontWeight:500,color:'#c95b3d'}}>Read ›</div>
        </div>
      </div>
    </div>
  );
};

const NoteCard = ({note, onClick}) => {
  const m    = ms(note.mood);
  const {time,full} = fmtDate(note.timestamp);
  return (
    <div
      onClick={onClick}
      style={{
        margin:'0 16px 8px',padding:'13px 14px 13px 18px',
        borderRadius:'14px',border:'1px solid #e8e2d6',background:'#fff',
        cursor:'pointer',display:'flex',gap:'12px',alignItems:'flex-start',
        position:'relative',overflow:'hidden',
        WebkitTapHighlightColor:'transparent',
        transition:'transform .18s',
      }}
      onTouchStart={e=>e.currentTarget.style.transform='scale(.98)'}
      onTouchEnd={e=>e.currentTarget.style.transform='scale(1)'}
    >
      <div style={{position:'absolute',left:0,top:0,bottom:0,width:'3px',background:m.bar,borderRadius:'0'}}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:'13px',color:'#2e2820',lineHeight:'1.65',fontWeight:300,marginBottom:'7px'}}>
          {note.text}
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          <div style={{fontSize:'10px',color:'#bfb5a6'}}>{full} · {time}</div>
          <div style={{fontSize:'10px',fontWeight:500,padding:'2px 8px',borderRadius:'10px',background:m.bg,color:m.c}}>
            {note.mood}
          </div>
        </div>
      </div>
    </div>
  );
};

const EntryDetail = ({entry, onBack, type='diary'}) => {
  const m    = ms(entry.mood);
  const {dow,full,time} = fmtDate(entry.timestamp);
  const isNote = type==='note';
  const bodyText = entry.text || entry.entry || '';

  return (
    <div style={{paddingBottom:'24px',background:'#faf8f3',minHeight:'100%'}}>
      {/* back */}
      <div style={{padding:'12px 16px 4px'}}>
        <button
          onClick={onBack}
          style={{
            display:'flex',alignItems:'center',gap:'6px',
            background:'none',border:'none',color:'#8c7f6d',
            cursor:'pointer',fontSize:'14px',fontWeight:500,
            fontFamily:'inherit',padding:'8px 0',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          Back
        </button>
      </div>

      {/* hero card */}
      <div style={{
        margin:'0 16px 14px',borderRadius:'20px',padding:'20px',
        background:m.bg,border:`1px solid ${m.c}25`,overflow:'hidden',
      }}>
        <div style={{fontSize:'11px',fontWeight:500,letterSpacing:'.04em',textTransform:'uppercase',color:m.c,marginBottom:'8px',opacity:.8}}>
          {dow && `${dow}, `}{full}{time && ` · ${time}`}
        </div>
        <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'24px',fontWeight:900,color:'#1a1612',lineHeight:1.2,marginBottom:'12px',letterSpacing:'-.02em'}}>
          {isNote ? 'Quick Note' : (entry.title||'Untitled')}
        </div>
        <div style={{fontSize:'11px',fontWeight:500,padding:'4px 12px',borderRadius:'20px',display:'inline-block',background:'rgba(255,255,255,.6)',color:m.c}}>
          {m.e} {entry.mood}
        </div>
      </div>

      {/* body — white-space:pre-wrap preserves every space and newline */}
      <div style={{margin:'0 16px 14px',padding:'22px 20px',background:'#fff',borderRadius:'18px',border:'1px solid #e8e2d6'}}>
        <p style={{
          fontFamily:'"Playfair Display",Georgia,serif',
          fontStyle:'italic',
          fontSize:'15px',
          lineHeight:2,
          color:'#2e2820',
          margin:0,
          whiteSpace:'pre-wrap',
          wordBreak:'break-word',
          overflowWrap:'break-word',
        }}>
          {bodyText}
        </p>
      </div>

      {/* insight */}
      {entry.insight && (
        <div style={{margin:'0 16px 14px',borderRadius:'18px',border:'1px solid #e8e2d6',overflow:'hidden'}}>
          <div style={{padding:'12px 16px',background:'#e6eef8',display:'flex',alignItems:'center',gap:'8px'}}>
            <div style={{width:'6px',height:'6px',borderRadius:'50%',background:'#1a4f8a',flexShrink:0}}/>
            <div style={{fontSize:'11px',fontWeight:500,letterSpacing:'.08em',textTransform:'uppercase',color:'#1a4f8a'}}>
              Saksham's insight
            </div>
          </div>
          <div style={{padding:'16px',fontSize:'13px',lineHeight:'1.75',color:'#2e2820',fontWeight:300}}>
            {entry.insight}
          </div>
          {entry.mood==='crisis' && (
            <div style={{padding:'0 16px 16px'}}>
              <a href="tel:9152987821" style={{
                display:'block',padding:'12px',background:'#1a4f8a',color:'#fff',
                borderRadius:'12px',textDecoration:'none',textAlign:'center',
                fontSize:'14px',fontWeight:500,
              }}>
                📞 iCall — 9152987821
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DaySection = ({group, onEntryClick, type}) => (
  <div style={{marginTop:'20px'}}>
    {/* day divider */}
    <div style={{
      display:'flex',alignItems:'center',gap:'12px',
      padding:'0 16px 12px',
      position:'sticky',top:'0',
      background:'linear-gradient(#faf8f3 90%,transparent)',
      zIndex:2,paddingTop:'4px',
    }}>
      <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'26px',fontWeight:900,color:'#e8e2d6',lineHeight:1,flexShrink:0,minWidth:'28px'}}>
        {group.day}
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:'13px',fontWeight:500,color:'#5a4e3e',lineHeight:1.2}}>{group.dow}</div>
        <div style={{fontSize:'11px',color:'#bfb5a6'}}>{group.full}</div>
      </div>
      <div style={{fontSize:'10px',color:'#bfb5a6',padding:'3px 8px',borderRadius:'6px',background:'#f3efe6'}}>
        {group.items.length} {group.items.length===1?(type==='note'?'note':'entry'):(type==='note'?'notes':'entries')}
      </div>
    </div>

    {/* cards */}
    {group.items.map(item =>
      type === 'note'
        ? <NoteCard key={item.id} note={item} onClick={() => onEntryClick(item,'note')}/>
        : <EntryCard key={item.id} entry={item} onClick={() => onEntryClick(item,'diary')}/>
    )}
  </div>
);

const Skeleton = () => (
  <div style={{padding:'0 16px'}}>
    {[1,2,3].map(i => (
      <div key={i} style={{marginBottom:'12px',borderRadius:'18px',border:'1px solid #e8e2d6',overflow:'hidden'}}>
        <div style={{height:'3px',background:'#e8e2d6'}}/>
        <div style={{padding:'14px 16px'}}>
          <div style={{height:'18px',background:'#f3efe6',borderRadius:'6px',marginBottom:'10px',width:'75%',animation:'shimmer 1.4s infinite'}}/>
          <div style={{height:'13px',background:'#f3efe6',borderRadius:'5px',marginBottom:'6px',animation:'shimmer 1.4s infinite'}}/>
          <div style={{height:'13px',background:'#f3efe6',borderRadius:'5px',width:'60%',animation:'shimmer 1.4s infinite'}}/>
        </div>
      </div>
    ))}
  </div>
);

/* ══════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                 */
/* ══════════════════════════════════════════════════════════════ */
export default function DiaryVault() {
  const navigate = useNavigate();

  const [isPaid,        setIsPaid]        = useState(false);
  const [tierLoaded,    setTierLoaded]    = useState(false);
  const [theme,         setTheme]         = useState(localStorage.getItem('theme')||'light-theme');

  /* bottom tab: 'journal' | 'notes' */
  const [tab,           setTab]           = useState('journal');

  /* months sidebar */
  const [months,        setMonths]        = useState([]);
  const [selMonth,      setSelMonth]      = useState(null);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [selDay,        setSelDay]        = useState(null);

  /* diary entries */
  const [entries,       setEntries]       = useState([]);
  const [entriesLoading,setEntriesLoading]= useState(false);

  /* quick notes */
  const [notes,         setNotes]         = useState([]);
  const [notesLoading,  setNotesLoading]  = useState(false);

  /* detail view */
  const [selEntry,      setSelEntry]      = useState(null);
  const [selType,       setSelType]       = useState('diary');

  /* day scroll refs */
  const dayRefs = useRef({});
  const contentRef = useRef(null);

  /* ─── Google Fonts ─────────────────────────────────────────── */
  useEffect(() => {
    document.body.className = theme;
    if (!document.getElementById('hh-gfonts')) {
      const l = document.createElement('link');
      l.id   = 'hh-gfonts';
      l.rel  = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500&display=swap';
      document.head.appendChild(l);
    }
    /* shimmer keyframe */
    if (!document.getElementById('hh-vault-styles')) {
      const s = document.createElement('style');
      s.id = 'hh-vault-styles';
      s.textContent = `
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
        .hh-vault-fade{animation:fadeUp .28s ease both}
        .hh-vault-slide{animation:slideIn .25s ease both}
      `;
      document.head.appendChild(s);
    }
  }, [theme]);

  /* ─── Init ─────────────────────────────────────────────────── */
  useEffect(() => {
    const init = async () => {
      if (!auth.currentUser) return;
      const claims = await getUserClaims();
      setIsPaid(claims.paid);
      setTierLoaded(true);
      await loadMonths();
    };
    init();
  }, []);

  const loadMonths = async () => {
    setMonthsLoading(true);
    try {
      const res  = await authFetch('/api/diary/months');
      const data = await res.json();
      const list = data.months || [];
      setMonths(list);
      if (list.length > 0) setSelMonth(list[0].month);
    } catch { /**/ }
    finally { setMonthsLoading(false); }
  };
  useEffect(() => {
    if (!selMonth) return;
    setSelDay(null); // Reset day filter when switching views
    if (tab === 'journal') loadEntries(selMonth);
    else                   loadNotes(selMonth);
  }, [selMonth, tab]);

  const loadEntries = async m => {
    setEntriesLoading(true); setEntries([]); setSelEntry(null);
    try {
      const res  = await authFetch(`/api/diary/month?month=${m}`);
      const data = await res.json();
      setEntries(data.entries||[]);
    } catch { /**/ }
    finally { setEntriesLoading(false); }
  };

  const loadNotes = async m => {
    setNotesLoading(true); setNotes([]); setSelEntry(null);
    try {
      const res  = await authFetch(`/api/quick-notes/month?month=${m}`);
      const data = await res.json();
      setNotes(data.notes||[]);
    } catch { /**/ }
    finally { setNotesLoading(false); }
  };

  const toggleTheme = () => {
    const ts = ['light-theme','dark-theme','zen-theme'];
    const next = ts[(ts.indexOf(theme)+1)%ts.length];
    setTheme(next); localStorage.setItem('theme',next); document.body.className=next;
  };
  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  const openEntry = (entry, type) => { setSelEntry(entry); setSelType(type); };
  const closeEntry = () => setSelEntry(null);

const toggleDayFilter = d => {
    // If they click the already selected day, clear the filter. Otherwise, filter by it.
    setSelDay(prev => prev === d ? null : d);
  };
  const grouped  = groupByDay(tab==='journal' ? entries : notes);
  // If a day is selected, ONLY show that day. Otherwise, show everything.
  const displayGroups = selDay ? grouped.filter(g => g.day === selDay) : grouped;
  const isLoading= tab==='journal' ? entriesLoading : notesLoading;
  const activeDays = (tab==='journal' ? entries : notes)
    .map(e => new Date(e.timestamp).getDate())
    .filter((v,i,a)=>a.indexOf(v)===i);

  const selMonthMeta = months.find(m=>m.month===selMonth);

  /* ──── STYLES ───────────────────────────────────────────────── */
  const S = {
    root:{
      minHeight:'100vh',background:'#faf8f3',
      fontFamily:'"DM Sans",system-ui,sans-serif',
      maxWidth:'480px',margin:'0 auto',
    },
    /* sticky top nav */
    topNav:{
      position:'sticky',top:0,zIndex:20,
      background:'#faf8f3',
      borderBottom:'0.5px solid #e8e2d6',
      display:'flex',alignItems:'center',justifyContent:'space-between',
      padding:'12px 16px',
    },
    /* month scroll */
    monthPill:(active) => ({
      display:'flex',flexDirection:'column',alignItems:'center',gap:'3px',
      padding:'10px 14px',borderRadius:'14px',
      background:active?'#1a1612':'#f3efe6',
      border:`1px solid ${active?'#1a1612':'#e8e2d6'}`,
      cursor:'pointer',transition:'all .2s',flexShrink:0,minWidth:'72px',
      WebkitTapHighlightColor:'transparent',
    }),
    /* bottom tabs */
    bottomNav:{
      position:'sticky',bottom:0,
      background:'#faf8f3',borderTop:'0.5px solid #e8e2d6',
      display:'flex',zIndex:20,
      paddingBottom:'env(safe-area-inset-bottom,0px)',
    },
    tabBtn:(active) => ({
      flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px',
      padding:'10px 4px',border:'none',background:'transparent',
      cursor:'pointer',WebkitTapHighlightColor:'transparent',
      fontFamily:'inherit',
    }),
  };

  /* ─── Detail view overlay ───────────────────────────────────── */
  if (selEntry) {
    return (
      <div style={{...S.root,overflowY:'auto'}} className="hh-vault-slide">
        <EntryDetail entry={selEntry} onBack={closeEntry} type={selType}/>
      </div>
    );
  }

  return (
    <div style={S.root}>

      {/* ── TOP NAV ──────────────────────────────────────────── */}
      <div style={S.topNav}>
        <Link to="/dashboard" style={{
          width:36,height:36,borderRadius:'50%',background:'#f3efe6',
          border:'1px solid #e8e2d6',display:'flex',alignItems:'center',
          justifyContent:'center',textDecoration:'none',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5a4e3e" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </Link>

        <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'18px',fontWeight:700,color:'#1a1612',letterSpacing:'-.01em'}}>
          {tab==='journal' ? 'My Journal' : 'Quick Notes'}
        </div>

        <button onClick={toggleTheme} style={{
          width:36,height:36,borderRadius:'50%',background:'#f3efe6',
          border:'1px solid #e8e2d6',display:'flex',alignItems:'center',
          justifyContent:'center',cursor:'pointer',fontSize:'16px',
        }}>
          {theme==='light-theme'?'☀️':theme==='dark-theme'?'🌙':'🌿'}
        </button>
      </div>

      {/* ── HERO HEADING ─────────────────────────────────────── */}
      <div style={{padding:'20px 20px 4px'}} className="hh-vault-fade">
        <div style={{fontSize:'12px',color:'#a0917d',marginBottom:'4px',fontWeight:400}}>
          {months.reduce((s,m)=>s+m.count,0)} entries · {months.length} months
        </div>
        <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'30px',fontWeight:900,color:'#1a1612',lineHeight:1.1,letterSpacing:'-.02em'}}>
          {selMonthMeta?.label || 'Loading...'}
          <span style={{fontSize:'16px',fontWeight:400,color:'#a0917d',marginLeft:'8px'}}>
            {selMonthMeta?.shortLabel?.split(' ')[1]}
          </span>
        </div>
      </div>

      {/* ── MONTH PILLS ──────────────────────────────────────── */}
      <div style={{display:'flex',gap:'10px',padding:'14px 16px',overflowX:'auto',scrollbarWidth:'none'}}>
        {monthsLoading
          ? [1,2,3,4].map(i=>(
              <div key={i} style={{width:72,height:58,borderRadius:14,background:'#f3efe6',flexShrink:0,animation:'shimmer 1.4s infinite'}}/>
            ))
          : months.map(m => {
              const mood = MOOD[m.dominantMood] || MOOD.neutral;
              const active = m.month === selMonth;
              return (
                <div key={m.month} style={S.monthPill(active)} onClick={()=>setSelMonth(m.month)}>
                  <span style={{fontSize:'18px',lineHeight:1}}>{mood.e}</span>
                  <div style={{fontSize:'11px',fontWeight:500,color:active?'rgba(255,255,255,.8)':'#5a4e3e',lineHeight:1.2}}>
                    {m.label.split(' ')[0]}
                  </div>
                  <div style={{fontSize:'10px',color:active?'rgba(255,255,255,.4)':'#bfb5a6'}}>{m.count}</div>
                </div>
              );
            })
        }
      </div>

      {/* ── MINI CALENDAR ────────────────────────────────────── */}
      {selMonth && !isLoading && grouped.length>0 && (
        <div style={{margin:'0 16px 4px',borderRadius:'14px',border:'1px solid #e8e2d6',background:'#fff',padding:'12px 0 4px'}}>
          <div style={{fontSize:'10px',fontWeight:500,color:'#a0917d',letterSpacing:'.08em',textTransform:'uppercase',padding:'0 16px 8px'}}>
            {selMonthMeta?.label}
          </div>
          <MiniCalendar month={selMonth} activeDays={activeDays} selDay={selDay} onDayClick={toggleDayFilter}/>
        </div>
      )}

      {/* ── CONTENT ─────────────────────────────────────────── */}
      <div ref={contentRef} style={{paddingBottom:'100px'}}>

        {/* Quick notes gate for free users */}
        {tab==='notes' && !isPaid && tierLoaded && (
          <div style={{margin:'16px',borderRadius:'20px',border:'1.5px dashed #e8e2d6',padding:'32px 20px',textAlign:'center'}}>
            <div style={{
              width:52,height:52,borderRadius:'50%',background:'#f3efe6',
              border:'1px solid #e8e2d6',display:'flex',alignItems:'center',
              justifyContent:'center',fontSize:'22px',margin:'0 auto 14px',
            }}>⚡</div>
            <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'18px',fontWeight:700,color:'#1a1612',marginBottom:'8px'}}>
              Saved quick notes
            </div>
            <div style={{fontSize:'13px',color:'#8c7f6d',lineHeight:'1.7',maxWidth:'240px',margin:'0 auto 18px',fontWeight:300}}>
              Upgrade to view your full mood note history, organized by month and day.
            </div>
            <button
              onClick={()=>navigate('/pricing')}
              style={{
                width:'100%',padding:'14px',background:'#1a1612',color:'#faf8f3',
                border:'none',borderRadius:'14px',fontSize:'14px',fontWeight:500,
                cursor:'pointer',fontFamily:'inherit',marginBottom:'8px',
              }}
            >
              Upgrade to Plus — ₹35/month
            </button>
            <div style={{fontSize:'11px',color:'#bfb5a6',fontWeight:300}}>28 days · No auto-renewal</div>

            {/* blurred preview */}
            <div style={{marginTop:'24px',filter:'blur(3px)',pointerEvents:'none',opacity:.7}}>
              {[
                {text:'Feeling lighter today, like something unclenched overnight',mood:'hopeful',time:'Sat, 21 Mar · 9:50 AM'},
                {text:"Anxious about tomorrow, can't stop thinking about it",mood:'anxious',time:'Fri, 20 Mar · 11:30 PM'},
                {text:'Good day. Nothing big, just good.',mood:'calm',time:'Wed, 18 Mar · 7:15 PM'},
              ].map((n,i)=>{
                const m=ms(n.mood);
                return (
                  <div key={i} style={{
                    marginBottom:'8px',padding:'13px 14px 13px 18px',
                    borderRadius:'14px',border:'1px solid #e8e2d6',background:'#fff',
                    textAlign:'left',position:'relative',overflow:'hidden',
                  }}>
                    <div style={{position:'absolute',left:0,top:0,bottom:0,width:'3px',background:m.bar}}/>
                    <div style={{fontSize:'13px',color:'#2e2820',lineHeight:'1.6',fontWeight:300,marginBottom:'5px'}}>{n.text}</div>
                    <div style={{fontSize:'10px',color:'#bfb5a6'}}>{n.time}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skeleton */}
        {isLoading && <Skeleton/>}

        {/* Empty state */}
        {!isLoading && tab==='journal' && entries.length===0 && selMonth && (
          <div style={{padding:'48px 32px',textAlign:'center'}}>
            <div style={{
              width:56,height:56,borderRadius:'18px',background:'#f3efe6',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:'22px',margin:'0 auto 16px',border:'1px solid #e8e2d6',
            }}>✍️</div>
            <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'18px',fontWeight:700,color:'#1a1612',marginBottom:'8px'}}>
              Nothing written yet
            </div>
            <div style={{fontSize:'13px',color:'#8c7f6d',lineHeight:'1.65',maxWidth:'240px',margin:'0 auto 20px',fontWeight:300}}>
              No diary entries for {selMonthMeta?.label}. Start writing from the Dashboard.
            </div>
            <button onClick={()=>navigate('/dashboard')} style={{
              padding:'12px 24px',background:'#c95b3d',color:'#fff',
              border:'none',borderRadius:'12px',fontSize:'14px',fontWeight:500,
              cursor:'pointer',fontFamily:'inherit',
            }}>
              Write today's entry →
            </button>
          </div>
        )}

        {!isLoading && tab==='notes' && isPaid && notes.length===0 && (
          <div style={{padding:'48px 32px',textAlign:'center'}}>
            <div style={{fontFamily:'"Playfair Display",Georgia,serif',fontSize:'18px',fontWeight:700,color:'#1a1612',marginBottom:'8px'}}>
              No quick notes for {selMonthMeta?.label}
            </div>
            <div style={{fontSize:'13px',color:'#8c7f6d',fontWeight:300}}>Log moods from the Dashboard to see them here.</div>
          </div>
        )}

{/* Day groups */}
        {!isLoading && selDay && (
          <div className="hh-vault-fade" style={{padding: '0 16px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{fontSize: '13px', color: '#8c7f6d'}}>
              Showing entries for <strong>{selMonthMeta?.shortLabel?.split(' ')[0]} {selDay}</strong>
            </div>
            <button 
              onClick={() => setSelDay(null)} 
              style={{
                background:'none', border:'none', color:'#c95b3d', 
                fontSize:'13px', fontWeight:500, cursor:'pointer',
                padding: '4px 8px', borderRadius: '6px', backgroundColor: '#fdf2ee'
              }}
            >
              Clear filter ✕
            </button>
          </div>
        )}

        {!isLoading && displayGroups.map((g,i) => (
          <div
            key={`${g.day}-${g.dow}`}
            ref={el => dayRefs.current[g.day] = el}
            className="hh-vault-fade"
            style={{animationDelay:`${i*0.06}s`}}
          >
            <DaySection group={g} onEntryClick={openEntry} type={tab==='notes'?'note':'diary'}/>
          </div>
        ))}
        {/* Free tier limit notice */}
        {!isPaid && tab==='journal' && entries.length>=10 && !isLoading && (
          <div style={{margin:'8px 16px 0',padding:'16px',background:'#fdf2ee',borderRadius:'14px',border:'1px solid #f5cbb8',textAlign:'center'}}>
            <div style={{fontSize:'13px',color:'#a05030',marginBottom:'10px'}}>
              Showing last 10 entries. Upgrade to access your full diary history.
            </div>
            <button onClick={()=>navigate('/pricing')} style={{
              padding:'10px 22px',background:'#c95b3d',color:'#fff',
              border:'none',borderRadius:'10px',fontSize:'13px',fontWeight:500,
              cursor:'pointer',fontFamily:'inherit',
            }}>
              Upgrade to Plus — ₹35/month
            </button>
          </div>
        )}
      </div>

      {/* ── BOTTOM TABS ─────────────────────────────────────── */}
      <div style={S.bottomNav}>
        {[
          {key:'journal',label:'Journal',
            icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>},
          {key:'notes',label:'Notes',
            icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>},
        ].map(t => {
          const active = tab===t.key;
          return (
            <button key={t.key} style={S.tabBtn(active)} onClick={()=>setTab(t.key)}>
              <div style={{
                width:28,height:28,borderRadius:'9px',display:'flex',
                alignItems:'center',justifyContent:'center',
                background:active?'#fdf2ee':'transparent',
                color:active?'#c95b3d':'#bfb5a6',
                transition:'all .18s',
              }}>
                {t.icon}
              </div>
              <div style={{fontSize:'10px',fontWeight:500,color:active?'#c95b3d':'#bfb5a6',transition:'color .18s'}}>
                {t.label}
              </div>
            </button>
          );
        })}
        <button style={S.tabBtn(false)} onClick={()=>navigate('/insight')}>
          <div style={{
            width:28,height:28,borderRadius:'9px',display:'flex',
            alignItems:'center',justifyContent:'center',
            color:'#bfb5a6',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div style={{fontSize:'10px',fontWeight:500,color:'#bfb5a6'}}>Report</div>
        </button>
        <button style={S.tabBtn(false)} onClick={()=>navigate('/dashboard')}>
          <div style={{
            width:28,height:28,borderRadius:'9px',display:'flex',
            alignItems:'center',justifyContent:'center',
            color:'#bfb5a6',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div style={{fontSize:'10px',fontWeight:500,color:'#bfb5a6'}}>Home</div>
        </button>
      </div>
    </div>
  );
}